package dev.lingvo.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.core.Page
import dev.lingvo.site.components.marketing.DocEntry
import dev.lingvo.site.components.marketing.DocsInlineLink
import dev.lingvo.site.components.marketing.DocsList
import dev.lingvo.site.components.marketing.DocsProse
import dev.lingvo.site.components.marketing.MarketingDocPage
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Text

/**
 * A free check: which App Store storefronts show this app's English listing.
 *
 * It runs entirely in the visitor's browser. The page asks Apple's public lookup endpoint (no sign-in,
 * no key) once per storefront; Lingvo's servers see nothing, and nothing is stored. Only an app id made
 * of digits is ever sent. Google Play has no public lookup, so this is Apple-only.
 */
@Page("/store-audit")
@Composable
fun StoreAuditPage() {
    LaunchedEffect(Unit) {
        document.title = tr("store_audit.page_title", "Free App Store localization check — Lingvo")
    }
    MarketingDocPage(
        eyebrow = t("store_audit.eyebrow", "Free tool"),
        title = t("store_audit.title", "Which App Store pages show your app in English only?"),
        lede = t(
            "store_audit.lede",
            "Paste your App Store link. We ask Apple's public listing service for each storefront and show where customers still read your English page.",
        ),
        entries = listOf(
            DocEntry("check", tr("store_audit.check_heading", "Check an app")) { StoreAuditTool() },
            DocEntry("how", tr("store_audit.how_heading", "How it works")) {
                DocsProse(t("store_audit.how_browser", "Your browser asks Apple directly, once for each storefront. Lingvo's servers never see the link or the result, and nothing is stored."))
                DocsProse(t("store_audit.how_compare", "A storefront counts as localized when Apple returns a description that differs from the US page. The same text means customers there read the default listing."))
                DocsList(
                    listOf(
                        t("store_audit.limit_apple_only", "Apple only. Google Play has no public lookup."),
                        t("store_audit.limit_text_only", "It compares listing text, not screenshots or keywords."),
                        t("store_audit.limit_markets", "It checks 38 storefronts whose customers mostly don't read English."),
                    ),
                )
            },
        ),
    )
}

@Composable
private fun StoreAuditTool() {
    val scope = rememberCoroutineScope()
    var input by remember { mutableStateOf("") }
    var running by remember { mutableStateOf(false) }
    var problem by remember { mutableStateOf<String?>(null) }
    var reference by remember { mutableStateOf<StorefrontListing?>(null) }
    val rows = remember { mutableStateListOf<AuditRow>() }

    fun run() {
        val appId = parseAppStoreId(input)
        if (appId == null) {
            problem = tr("store_audit.bad_link", "That doesn't look like an App Store link. Paste the app's page address, for example https://apps.apple.com/app/id123456789.")
            return
        }
        problem = null
        rows.clear()
        reference = null
        running = true
        scope.launch {
            try {
                val referenceListing = fetchListing(appId, AUDIT_REFERENCE_COUNTRY).getOrNull()
                reference = referenceListing
                AUDIT_STOREFRONTS.chunked(6).forEach { chunk ->
                    chunk.map { storefront ->
                        async {
                            val result = fetchListing(appId, storefront.country)
                            AuditRow(
                                storefront,
                                classify(referenceListing, result.getOrNull(), failed = result.isFailure),
                                result.getOrNull()?.title,
                            )
                        }
                    }.awaitAll().forEach { rows += it }
                }
            } finally {
                running = false
            }
        }
    }

    Column(Modifier.fillMaxWidth().maxWidth(44.cssRem).gap(Space.MD)) {
        LTextField(
            value = input,
            onValueChange = { input = it },
            label = t("store_audit.link_label", "App Store link or app id"),
            placeholder = "https://apps.apple.com/app/id123456789",
            enabled = !running,
        )
        LButton(
            t("store_audit.check_button", "Check storefronts"),
            onClick = { run() },
            enabled = input.isNotBlank() && !running,
            loading = running,
        )
        problem?.let { LAlert(t("store_audit.cant_check", "Can't check that"), it, variant = LAlertVariant.WARNING, live = true) }
        if (rows.isNotEmpty()) StoreAuditResult(rows.toList(), reference != null, running)
    }
}

@Composable
private fun StoreAuditResult(rows: List<AuditRow>, hasReference: Boolean, running: Boolean) {
    val summary = summarize(rows)
    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
        if (!hasReference) {
            LAlert(
                t("store_audit.no_us_title", "Not on the US storefront"),
                t("store_audit.no_us_detail", "The app isn't on sale in the US, so there is no English page to compare with. Every storefront that has it is shown as found."),
                variant = LAlertVariant.INFO,
                dense = true,
            )
        }
        Text(
            tf(
                "store_audit.summary",
                "{same} of {total} storefronts still show your default listing; {localized} show a localized page.",
                "same" to summary.sameAsReference,
                "total" to (summary.checked - summary.notAvailable - summary.failed),
                "localized" to summary.localized,
            ),
        )
        if (running) LMuted(t("store_audit.checking", "Still checking…"))
        LTable(caption = t("store_audit.table_caption", "App Store storefronts"), captionHidden = true) {
            LTableHead {
                LTableRow {
                    LTableHeaderCell { Text(t("store_audit.col_storefront", "Storefront")) }
                    LTableHeaderCell { Text(t("store_audit.col_page", "Page")) }
                }
            }
            LTableBody {
                rows.forEach { row ->
                    LTableRow {
                        LTableCell { Text(row.storefront.name) }
                        LTableCell { LBadge(auditVerdictLabel(row.verdict), variant = auditVerdictVariant(row.verdict)) }
                    }
                }
            }
        }
        if (summary.sameAsReference > 0 && !running) {
            DocsProse {
                Text(
                    tPlural(
                        "store_audit.cta",
                        summary.sameAsReference,
                        "{count} storefront shows your default text. ",
                        "{count} storefronts show your default text. ",
                    ),
                )
                DocsInlineLink("/signup?source=other", t("store_audit.cta_link", "Translate it with your glossary, review it, and push it from Lingvo"))
            }
        }
    }
}

private fun auditVerdictLabel(verdict: AuditVerdict): String = when (verdict) {
    AuditVerdict.LOCALIZED -> tr("store_audit.verdict_localized", "Localized")
    AuditVerdict.SAME_AS_REFERENCE -> tr("store_audit.verdict_same", "Default listing")
    AuditVerdict.NOT_AVAILABLE -> tr("store_audit.verdict_unavailable", "Not on sale")
    AuditVerdict.FAILED -> tr("store_audit.verdict_failed", "Couldn't check")
}

private fun auditVerdictVariant(verdict: AuditVerdict): LBadgeVariant = when (verdict) {
    AuditVerdict.LOCALIZED -> LBadgeVariant.SUCCESS
    AuditVerdict.SAME_AS_REFERENCE -> LBadgeVariant.WARNING
    AuditVerdict.NOT_AVAILABLE -> LBadgeVariant.NEUTRAL
    AuditVerdict.FAILED -> LBadgeVariant.NEUTRAL
}

/** Failure when Apple did not answer; success with null when the app is not in that storefront. */
private suspend fun fetchListing(appId: String, country: String): Result<StorefrontListing?> = runCatching {
    val response = window.fetch(lookupUrl(appId, country)).await()
    check(response.ok) { "Apple answered ${response.status}" }
    parseLookup(response.text().await())
}
